import { inject, Injectable } from '@angular/core';
import {
  RequirementDocumentLink,
  RoutineChecklistRequirementResult,
  RoutineChecklistRequirementResultsRequest,
  RoutineComplianceChecklistSubmissionsData,
  RoutineComplianceChecklistSubmissionsRequest,
} from '../models/inspection-checklist.model';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Response } from '../../../../_shared/models';
import { KpiStatsResponse } from '../../../../_shared/models/kpi-response.model';
import { PaginatedResult } from '../../../../_shared/models/paginated-result.model';
import {
  ComplianceCalendarEventsByDateApiRequest,
  ComplianceCalendarEventsPreviewApiRequest,
  ComplianceCalendarEventResponse,
  ComplianceOverviewRequirement,
  ComplianceOverviewRequirementsApiRequest,
  TodayComplianceCalendarEventResponse,
  TodayComplianceCalendarEventsApiRequest,
  ComplianceCalendarEventScheduleResponse,
  ComplianceCalendarEventScheduleApiRequest,
  ComplianceOverviewRequirementAsset,
  ComplianceOverviewRequirementAssetsApiRequest,
  UpdateComplianceCalendarEventScheduleApiRequest,
  AssignRoutineComplianceTaskWorkshopRequest,
  AssetComplianceChecklistResult,
  AssetComplianceChecklistResultsRequest,
  AssetComplianceChecklistSummaryRequest,
  ComplianceRegion,
  PeriodicComplianceTask,
  PeriodicComplianceTasksRequest,
  EditPeriodicComplianceTaskRequest,
  ReschedulePeriodicComplianceTaskRequest,
  RescheduleRoutineComplianceTaskRequest,
  DeletePeriodicComplianceTaskRequest,
  DeleteRoutineComplianceTaskRequest,
  RoutineComplianceTask,
  RoutineComplianceTasksRequest,
} from '../models/compliance-item.model';
import { SubmissionVideoKind, SubmissionVideoLink } from '../models/routine-video.model';

@Injectable({
  providedIn: 'root'
})
export class ComplianceOverviewService {
  private http: HttpClient = inject(HttpClient);

  getCompliancesRequirementsAsync(
    request: ComplianceOverviewRequirementsApiRequest,
    cancellation: Subject<void>
  ): Promise<Response<PaginatedResult<ComplianceOverviewRequirement>>> {
    return this.postCancellable('compliance-overview/requirements/filter', request, cancellation);
  }

  getComplianceOverviewRequirementAssetsAsync(
    request: ComplianceOverviewRequirementAssetsApiRequest,
    cancellation: Subject<void>
  ): Promise<Response<PaginatedResult<ComplianceOverviewRequirementAsset>>> {
    return this.postCancellable('compliance-overview/requirements/assets/filter', request, cancellation);
  }

  getTodayComplianceCalendarEventsAsync(
    request: TodayComplianceCalendarEventsApiRequest,
    cancellation: Subject<void>
  ): Promise<Response<PaginatedResult<TodayComplianceCalendarEventResponse>>> {
    return this.postCancellable('compliance-overview/calendar-events/today', request, cancellation);
  }

  getComplianceCalendarEventsPreviewAsync(
    request: ComplianceCalendarEventsPreviewApiRequest,
    cancellation: Subject<void>
  ): Promise<Response<ComplianceCalendarEventResponse[]>> {
    return this.postCancellable('compliance-overview/calendar-events/preview', request, cancellation);
  }

  getComplianceCalendarEventsByDateAsync(
    request: ComplianceCalendarEventsByDateApiRequest,
    cancellation: Subject<void>
  ): Promise<Response<ComplianceCalendarEventResponse[]>> {
    return this.postCancellable('compliance-overview/calendar-events/date', request, cancellation);
  }

  getComplianceCalendarEventScheduleAsync(
    request: ComplianceCalendarEventScheduleApiRequest,
    cancellation: Subject<void>
  ): Promise<Response<ComplianceCalendarEventScheduleResponse>> {
    const params = new URLSearchParams({
      taskType: request.taskType,
      sourceTaskId: request.sourceTaskId.toString()
    });

    if (request.phase) {
      params.set('phase', request.phase);
    }

    return this.getCancellable(`compliance-overview/event-schedule?${params.toString()}`, cancellation);
  }

  updateComplianceCalendarEventScheduleAsync(
    request: UpdateComplianceCalendarEventScheduleApiRequest,
    cancellation: Subject<void>
  ): Promise<Response<boolean>> {
    return this.postCancellable('compliance-overview/event-schedule', request, cancellation);
  }

  async getComplianceRegionsAsync(cancellation: Subject<void>): Promise<Response<ComplianceRegion[]>> {
    return firstValueFrom(
      this.http.get<Response<ComplianceRegion[]>>(`${environment.apiEndpoint}/compliance-library/countries-and-regions`).pipe(takeUntil(cancellation))
    );
  }

  async getRoutineComplianceTasksAsync(
    request: RoutineComplianceTasksRequest,
    cancellation: Subject<void>
  ): Promise<Response<PaginatedResult<RoutineComplianceTask>>> {
    return this.postCancellable('routinecompliance/tasks/filter', request, cancellation);
  }

  async assignRoutineComplianceTaskWorkshopAsync(
    request: AssignRoutineComplianceTaskWorkshopRequest,
    cancellation: Subject<void>
  ): Promise<Response<boolean>> {
    return this.postCancellable('routinecompliance/assign-workshop', request, cancellation);
  }

  async getRoutineChecklistRequirementResultsAsync(
    request: RoutineChecklistRequirementResultsRequest,
    cancellation: Subject<void>
  ): Promise<Response<RoutineChecklistRequirementResult[]>> {
    return this.postCancellable('routinecompliance/checklist-requirement-results/filter', request, cancellation);
  }

  async getRequirementDocumentLinksAsync(complianceId: number): Promise<Response<RequirementDocumentLink[]>> {
    const params = new HttpParams().set('complianceId', complianceId);
    return firstValueFrom(
      this.http.get<Response<RequirementDocumentLink[]>>(
        `${environment.apiEndpoint}/compliance-library/requirement-document/links`,
        { params }
      )
    );
  }

  async getChecklistAttachmentDownloadLinkAsync(
    submissionId: number,
    checklistId: number,
    attachmentReference: string
  ): Promise<Response<string>> {
    const params = new HttpParams()
      .set('submissionId', submissionId)
      .set('checklistId', checklistId)
      .set('attachmentReference', attachmentReference);

    return firstValueFrom(
      this.http.get<Response<string>>(
        `${environment.apiEndpoint}/routinecompliance/checklist-requirement-results/attachment-download`,
        { params }
      )
    );
  }

  async getSubmissionVideoLinkAsync(
    routineComplianceTaskId: number,
    inspectionTypeId: number,
    videoKind: SubmissionVideoKind
  ): Promise<Response<SubmissionVideoLink>> {
    return firstValueFrom(
      this.http.get<Response<SubmissionVideoLink>>(
        `${environment.apiEndpoint}/routinecompliance/submission-video-link`,
        { params: this.buildSubmissionVideoParams(routineComplianceTaskId, inspectionTypeId, videoKind) }
      )
    );
  }

  async getSubmissionVideoDownloadLinkAsync(
    routineComplianceTaskId: number,
    inspectionTypeId: number,
    videoKind: SubmissionVideoKind
  ): Promise<Response<string>> {
    return firstValueFrom(
      this.http.get<Response<string>>(
        `${environment.apiEndpoint}/routinecompliance/submission-video-download`,
        { params: this.buildSubmissionVideoParams(routineComplianceTaskId, inspectionTypeId, videoKind) }
      )
    );
  }

  private buildSubmissionVideoParams(
    routineComplianceTaskId: number,
    inspectionTypeId: number,
    videoKind: SubmissionVideoKind
  ): HttpParams {
    return new HttpParams()
      .set('routineComplianceTaskId', routineComplianceTaskId)
      .set('inspectionTypeId', inspectionTypeId)
      .set('videoKind', videoKind);
  }

  async getRoutineComplianceChecklistAsync(
    request: RoutineComplianceChecklistSubmissionsRequest,
    cancellation: Subject<void>
  ): Promise<Response<RoutineComplianceChecklistSubmissionsData>> {
    return this.postCancellable('routinecompliance/checklist-submissions', request, cancellation);
  }

  async getPeriodicComplianceTasksAsync(request: PeriodicComplianceTasksRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<PeriodicComplianceTask>>> {
    return this.postCancellable('periodiccompliance/tasks/filter', request, cancellation);
  }

  async editPeriodicComplianceTaskAsync(request: EditPeriodicComplianceTaskRequest): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.post(`${environment.apiEndpoint}/periodiccompliance/tasks/edit`, request)
    );
  }

  async deletePeriodicComplianceTaskAsync(request: DeletePeriodicComplianceTaskRequest): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.delete(`${environment.apiEndpoint}/periodiccompliance/tasks/delete`, { body: request })
    );
  }

  async reschedulePeriodicComplianceTaskAsync(request: ReschedulePeriodicComplianceTaskRequest): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.post(`${environment.apiEndpoint}/periodiccompliance/tasks/reschedule`, request)
    );
  }

  async rescheduleRoutineComplianceTaskAsync(
    request: RescheduleRoutineComplianceTaskRequest,
    cancellation: Subject<void>
  ): Promise<Response<boolean>> {
    return this.postCancellable('routinecompliance/tasks/reschedule', request, cancellation);
  }

  async deleteRoutineComplianceTaskAsync(request: DeleteRoutineComplianceTaskRequest): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.delete<Response<boolean>>(`${environment.apiEndpoint}/routinecompliance/tasks/delete`, { body: request })
    );
  }

  async getAssetComplianceChecklistResultsAsync(request: AssetComplianceChecklistResultsRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<AssetComplianceChecklistResult>>> {
    return firstValueFrom(
      this.http.post(`${environment.apiEndpoint}/periodiccompliance/checklist-results/filter`, request).pipe(takeUntil(cancellation))
    );
  }

  async getAssetCompliancePassSummaryAsync(request: AssetComplianceChecklistSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return this.postCancellable('periodiccompliance/checklist-results/passing-summary', request, cancellation);
  }

  async getAssetComplianceFailSummaryAsync(request: AssetComplianceChecklistSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return this.postCancellable('periodiccompliance/checklist-results/failing-summary', request, cancellation);
  }

  async getAssetCompliancePendingSummaryAsync(request: AssetComplianceChecklistSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return this.postCancellable('periodiccompliance/checklist-results/pending-summary', request, cancellation);
  }

  async getAssetComplianceInspectionPassRateSummaryAsync(request: AssetComplianceChecklistSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return this.postCancellable('periodiccompliance/checklist-results/inspection-pass-rate-summary', request, cancellation);
  }

  async getAssetRoutineCompliancePassSummaryAsync(request: AssetComplianceChecklistSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return this.postCancellable('routinecompliance/checklist-results/passing-summary', request, cancellation);
  }

  async getAssetRoutineComplianceFailSummaryAsync(request: AssetComplianceChecklistSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return this.postCancellable('routinecompliance/checklist-results/failing-summary', request, cancellation);
  }

  async getAssetRoutineCompliancePendingSummaryAsync(request: AssetComplianceChecklistSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return this.postCancellable('routinecompliance/checklist-results/pending-summary', request, cancellation);
  }

  async getAssetRoutineComplianceInspectionPassRateSummaryAsync(request: AssetComplianceChecklistSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return this.postCancellable('routinecompliance/checklist-results/inspection-pass-rate-summary', request, cancellation);
  }

  private postCancellable<TReq, TRes>(path: string, body: TReq, cancellation: Subject<void>): Promise<Response<TRes>> {
    return firstValueFrom(
      this.http.post<Response<TRes>>(`${environment.apiEndpoint}/${path}`, body).pipe(takeUntil(cancellation))
    );
  }

  private getCancellable<TRes>(path: string, cancellation: Subject<void>): Promise<Response<TRes>> {
    return firstValueFrom(
      this.http.get<Response<TRes>>(`${environment.apiEndpoint}/${path}`).pipe(takeUntil(cancellation))
    );
  }
}